import { CreateChatRoomAPI } from "../api/chat/getChatRooms";
import socket from "../config/socket.io/socket.io";
import { store } from "../store";
import type { ChatRoom } from "@/utils/chatFormat";

let presenceUserId: string | null = null;
let presenceConnectHandlerAttached = false;

/**
 * Register the current user's presence with the chat server.
 *
 * Safe to call multiple times (chat list + message thread): the socket
 * "connect" handler is attached only once and re-emits presence on every
 * reconnect, so the user stays online across the reconnection attempts.
 */
export const registerPresence = (userId: string) => {
  if (!userId) return;
  presenceUserId = userId;
  if (!presenceConnectHandlerAttached) {
    presenceConnectHandlerAttached = true;
    socket.on("connect", () => {
      if (presenceUserId) {
        socket.emit("presence", { user_id: presenceUserId });
      }
    });
  }
  if (socket.connected) {
    socket.emit("presence", { user_id: presenceUserId });
  }
};

/**
 * Create (or find) a chat room and join it over the socket, so the caller can
 * navigate to the message thread. For `type = "admin"` `receiver_user_id` may
 * be omitted — the backend resolves the admin.
 *
 * @param params { receiver_user_id?, type, coupon_id?, sponsor_activity_id? }
 * @returns the created/found room, or null on failure
 */
export const openChatRoom = async (params: {
  receiver_user_id?: string;
  type: string;
  coupon_id?: string;
  sponsor_activity_id?: string;
}): Promise<ChatRoom | null> => {
  const { user } = store.getState().auth as any;
  if (!user?.id) return null;

  const resp = await CreateChatRoomAPI({
    user_id: user.id,
    receiver_user_id: params.receiver_user_id,
    type: params.type,
    coupon_id: params.coupon_id,
    sponsor_activity_id: params.sponsor_activity_id,
  });

  if (resp?.status === 0 && resp.result?.id) {
    socket.emit("joinRoom", {
      chat_room_id: resp.result.id,
      user_id: user.id,
    });
    return resp.result as ChatRoom;
  }
  return null;
};
